import { CustomerStatus } from '@ruta/shared';

/**
 * Customer status is a Typesense facet, not a sort field. Equal-name twins
 * therefore keep Typesense order (text match, then last name, then created-at)
 * unless we re-rank after search: Active before Inactive, then balance due
 * (RUT-3654). Do not add status to Typesense sort_by — that would lift every
 * Active hit above every Inactive hit, including unrelated names.
 */

export interface RankableSearchCustomer {
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  status?: string | null;
  balance?: number | null;
}

function nameKey(customer: RankableSearchCustomer): string {
  const first = (customer.firstName ?? '').trim().toLowerCase();
  const last = (customer.lastName ?? '').trim().toLowerCase();
  if (first || last) return `${first}\u0000${last}`;
  return (customer.name ?? '').trim().toLowerCase();
}

function statusRank(status: string | null | undefined): number {
  const normalized = (status ?? '').toLowerCase();
  if (normalized === CustomerStatus.ACTIVE) return 0;
  if (normalized === CustomerStatus.INACTIVE) return 2;
  return 1;
}

function compareEqualMatch(a: RankableSearchCustomer, b: RankableSearchCustomer): number {
  const byStatus = statusRank(a.status) - statusRank(b.status);
  if (byStatus !== 0) return byStatus;
  return (b.balance ?? 0) - (a.balance ?? 0);
}

export function rankEqualMatchCustomers<T extends RankableSearchCustomer>(hits: T[]): T[] {
  const groups = new Map<string, T[]>();
  const order: string[] = [];

  for (const hit of hits) {
    const key = nameKey(hit);
    let group = groups.get(key);
    if (!group) {
      group = [];
      groups.set(key, group);
      order.push(key);
    }
    group.push(hit);
  }

  return order.flatMap((key) => {
    const group = groups.get(key);
    if (!group || group.length < 2) return group ?? [];
    return [...group].sort(compareEqualMatch);
  });
}
